import { Stack } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import { useContext, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { ResizableArea } from '@work4all/components/lib/components/ResizableArea';

import { OverlayFullscreenContext, useFormPlus, useUser } from '@work4all/data';

import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ProjectHoursBillingBy } from '@work4all/models/lib/Enums/ProjectHoursBillingBy.enum';

import { canAddInvoice } from '@work4all/utils/lib/permissions';

import { useProject } from '../../../../../containers/files/detail/components/queries/use-project';
import {
  Form,
  MaskOverlayHeader,
  MaskOverlayStandardSubmitButton,
} from '../../../../../containers/mask-overlays/mask-overlay/components';
import { MaskOverlayFullscreenToggleButton } from '../../../../../containers/mask-overlays/mask-overlay/components/MaskOverlayFullscreenToggleButton';
import {
  MaskContextProvider,
  useMaskConfig,
} from '../../../../../containers/mask-overlays/mask-overlay/hooks/mask-context';
import { bzObjToEntityType } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/simple-pdf-report/utils';
import {
  useBillProjectHours,
  useProjectHourBillingOverlayDefaultValues,
  useProjectHourBillingOverlayFormUpdate,
  useProjectHourBillingRelatedRecords,
} from '../hooks';
import { useInputsValid } from '../hooks/use-inputs-valid';

import { ProjectHourBillingOverlayBillableServices } from './ProjectHourBillingOverlayBillableServices';
import { ProjectHourBillingOverlayBillingType } from './ProjectHourBillingOverlayBillingType';
import { ProjectHourBillingOverlayMiscellaneous } from './ProjectHourBillingOverlayMiscellaneous';
import { ProjectHourBillingOverlayRelatedRecords } from './ProjectHourBillingOverlayRelatedRecords';
import { ProjectHourBillingPreview } from './ProjectHourBillingPreview';
import { ProjectHourBillingOverlayFormValue } from './types';

type Props = {
  id: number;
  projectTimeIds: number[];
  startDate?: string;
  endDate?: string;
  onAfterSave: () => void;
};

export const ProjectHourBillingOverlay = ({
  id,
  projectTimeIds,
  startDate,
  endDate,
  onAfterSave,
}: Props) => {
  const { t } = useTranslation();

  const { setSettingsKey } = useContext(OverlayFullscreenContext);

  useEffect(() => {
    setSettingsKey?.('billProjectHours');
  }, [setSettingsKey]);

  const dialogs = useDialogs();

  const user = useUser();
  const invoiceEnabled = canAddInvoice(user);

  const defaultValues = useProjectHourBillingOverlayDefaultValues({
    id,
    projectTimeIds,
    startDate,
    endDate,
    bzObjType: invoiceEnabled ? BzObjType.RECHNUNG : BzObjType.LIEFERSCHEIN,
  });

  const mask = useMaskConfig({ id, entity: Entities.billProjectHoursResult });

  const form = useFormPlus<ProjectHourBillingOverlayFormValue>({
    mode: 'onChange',
    defaultValues,
  });

  const projectData = useProject(id.toString());

  useProjectHourBillingOverlayFormUpdate(form);

  const settings = form.watch('settings');
  const filters = form.watch('filters');
  const { billingBy } = settings;
  const relatedRecords = useProjectHourBillingRelatedRecords(
    filters,
    billingBy
  );

  const [relatedRecordsAutoSet, setRelatedRecordsAutoSet] = useState(true);

  const firstRelatedRecord = relatedRecords?.[0];

  useEffect(() => {
    setRelatedRecordsAutoSet(true);
  }, [firstRelatedRecord]);

  useEffect(() => {
    if (relatedRecordsAutoSet && relatedRecords.length > 0) {
      if (settings.billingBy === ProjectHoursBillingBy.CUSTOMER) {
        form.setValue('settings', {
          ...settings,
          customerCodes: [relatedRecords[0].id],
        });
      }
      if (settings.billingBy === ProjectHoursBillingBy.ORDER_CONFIRMATION) {
        form.setValue('settings', {
          ...settings,
          orderConfirmationCodes: [relatedRecords[0].id],
        });
      }
      setRelatedRecordsAutoSet(false);
    }
  }, [form, relatedRecords, settings, relatedRecordsAutoSet]);

  const inputsValid = useInputsValid(form);

  const [width, setWidth] = useState(600);

  const [isLoading, setIsLoading] = useState(false);

  const [mutate] = useBillProjectHours();
  const onSubmit = useEventCallback(() => {
    dialogs.confirm({
      title: t('COMMON.SERVICE_BILLING.CONFIRM.TITLE'),
      description: t('COMMON.SERVICE_BILLING.CONFIRM.MESSAGE'),
      onConfirm: async () => {
        setIsLoading(true);
        const result = await mutate({
          variables: {
            filters: form.getValues().filters,
            settings: form.getValues().settings,
          },
        });
        setIsLoading(false);

        if (result.data.billProjectHours.errors?.length > 0) {
          dialogs.alert({
            title: t('COMMON.SERVICE_BILLING.ERROR.TITLE'),
            description: result.data.billProjectHours.errors
              .map((error) => error.data)
              .join('\n'),
            onClose: () => {
              onAfterSave?.();
            },
          });
        } else {
          dialogs.alert({
            title: t('COMMON.SERVICE_BILLING.SUCCESS.TITLE'),
            description: t(
              'COMMON.SERVICE_BILLING.SUCCESS.MESSAGE' +
                (result.data.billProjectHours.createdBzObjectCodes.length === 1
                  ? '_ONE'
                  : ''),
              {
                count: result.data.billProjectHours.createdBzObjectCodes.length,
              }
            ),
            onClose: () => {
              onAfterSave?.();
            },
          });
        }
      },
    });
  });

  return (
    <FormProvider {...form}>
      <MaskContextProvider value={mask}>
        <Form
          onSubmit={form.handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
        >
          <MaskOverlayHeader
            title={t('COMMON.SERVICE_BILLING')}
            subTitle={projectData?.project?.name}
            actions={
              <>
                <MaskOverlayStandardSubmitButton
                  isDirty={true}
                  isLoading={isLoading}
                  disabled={!inputsValid}
                  title={t(`COMMON.SERVICE_BILLING.SUBMIT_BUTTON`, {
                    type: t(
                      `COMMON.${bzObjToEntityType[
                        settings.bzObjType
                      ].toUpperCase()}`,
                      {
                        count: 2,
                      }
                    ),
                  })}
                />
                <MaskOverlayFullscreenToggleButton />
              </>
            }
          />
          <Stack direction="row" width="100%" flex="1">
            <ResizableArea
              direction="horizontal"
              size={{ width }}
              handles={['right']}
              onResize={(size) => {
                setWidth(size.width);
              }}
            >
              <ProjectHourBillingOverlayBillableServices />
              <ProjectHourBillingOverlayBillingType
                relatedRecords={relatedRecords}
              />
              <ProjectHourBillingOverlayRelatedRecords data={relatedRecords} />

              <ProjectHourBillingOverlayMiscellaneous />
            </ResizableArea>
            <Stack flex={1} minWidth="38rem">
              <ProjectHourBillingPreview />
            </Stack>
          </Stack>
        </Form>
      </MaskContextProvider>
    </FormProvider>
  );
};
